
// Shared controller for editing per-observation-type LABEL BEHAVIORS (Free Entry / Max Count / Hidden, plus
// the per-type Auto-Filter/distilling switch). Mirrors the LabelManagerController pattern: a plain class (NOT a
// pict provider) driven by a host provider, exporting a shared template set (LABELBEHAVIOR_TEMPLATES) + CSS
// (LABELBEHAVIOR_CSS). Template handlers call back into the host by name via Record.ProviderName, so one
// template serves both hosts (the Project workspace and My Headlight).
//
// WHERE THESE LIVE (see memory observation-label-behaviors-storage): unlike the label VOCABULARY (ProjectTagType,
// edited by LabelManagerController), a label's BEHAVIOR is keyed per (ObservationType x TagType) inside the
// ObservationType "Configuration" cascade blob at PropertyInfo/<TagType>/<Flag>. The Auto-Filter switch is a
// per-observation-type flag in a SEPARATE blob (NamespaceKey 'DistillingService', NamespaceValue 'Library').
// Both are read/written with the standard Configuration/Get + Configuration/Put/<Namespace>/<Value>/<Scope>/<ID>
// endpoints the config app already uses (ConfigClone, PermissionMatrix). Reads return the merged-EFFECTIVE config
// at the scope; Put diffs the submitted config against the parent scope and stores only the overrides, so we
// READ-MERGE-WRITE the whole namespace-value config (never a partial — a key omitted from the submission is
// treated as "revert to inherited" and its override is deleted).
//
// Two scopes, same blobs:
//   - customer DEFAULT (My Headlight):  LevelContext 'Customer', ContextID <customer>   (IDProject 0)
//   - per-PROJECT (Project workspace):  LevelContext 'Project',  ContextID <project>
// The server (Configuration-Put-ByNamespace) authorizes Customer/Contract/Project writes for a CustomerAdmin
// role (or a Headlight super-user); Global is super-only. We gate the UI on the host's canManageLabels and
// surface any server refusal as a toast.

const libConfigSync = require('./ConfigSync.js');

const OBS_NAMESPACE = 'ObservationType';
const DISTILL_NAMESPACE = 'DistillingService';
const DISTILL_VALUE = 'Library';
// Config document keys that are not observation types (skip them when enumerating types).
const NON_TYPE_KEYS = new Set(['Config', 'LevelControlRecords', 'LevelControlRecord']);

class LabelBehaviorController
{
	/**
	 * @param {any} pPict
	 * @param {String} pProviderName - the host provider's identifier (the template calls it back by name).
	 */
	constructor(pPict, pProviderName)
	{
		this.pict = pPict;
		this.providerName = pProviderName;

		this.scope = { IDProject: 0, IDCustomer: 0 };
		this.canManage = false;
		this._onChange = () => {};

		// The full ObservationType config document at this scope, keyed by observation-type name; each value is
		// { ObservationType, PropertyInfo: { <TagType>: { EntityType, FreeEntry, MaxCount, Hidden, ... } }, ... }.
		this._doc = {};
		// The DistillingService 'Library' config: { DistillingEnabled: <scope default>, ByType: { <type>: { DistillingEnabled } } }.
		this._distill = { DistillingEnabled: false, ByType: {} };
		this.types = [];        // [{ Type, DisplayName, LabelCount }] — only types that carry >=1 label
		this.selectedType = '';
		this.loading = false;
		this.busy = false;
	}

	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	get modal()
	{
		return this.pict.views['Pict-Section-Modal'];
	}

	_toast(pMessage, pType)
	{
		if (this.modal && (typeof this.modal.toast === 'function')) { this.modal.toast(pMessage, { type: pType || 'success' }); }
	}

	/**
	 * After a behavior change, (debounced) publish this scope's config to the field apps (Fieldbook) — a Project
	 * sync for a project scope, a Customer sync for the customer-default scope. Same seam LabelManagerController
	 * uses; without it, a toggled flag is saved but never reaches Fieldbook. Debounced + permission-gated inside
	 * ConfigSync (a non-super-user without Module-PlatformConfiguration-Access gets a gentle notice instead).
	 */
	_requestConfigSync()
	{
		if (Number(this.scope.IDProject) > 0)
		{
			libConfigSync.requestSync(this.pict, 'Project', this.scope.IDProject);
		}
		else if (Number(this.scope.IDCustomer) > 0)
		{
			libConfigSync.requestSync(this.pict, 'Customer', this.scope.IDCustomer);
		}
	}

	// --- REST (promisified) ---

	_postJSON(pURL, pBody)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.postJSON(pURL, pBody, (pError, pBody2) => pError ? reject(pError) : resolve(pBody2));
		});
	}

	/** The Configuration cascade level for this scope: Project when a project is set, else Customer default. */
	_level()
	{
		return (Number(this.scope.IDProject) > 0)
			? { Context: 'Project', ContextID: Number(this.scope.IDProject) }
			: { Context: 'Customer', ContextID: Number(this.scope.IDCustomer) };
	}

	/** Read the merged-effective config for a namespace at this scope. Returns the document (unwrapped). */
	async _getConfig(pNamespaceKey)
	{
		const tmpLevel = this._level();
		const tmpBody = await this._postJSON('Configuration/Get',
			{ LevelContext: tmpLevel.Context, LevelContextID: tmpLevel.ContextID, NamespaceKey: pNamespaceKey });
		return (tmpBody && (tmpBody.Document || tmpBody)) || {};
	}

	/**
	 * Read-merge-write the WHOLE config for one namespace value at this scope. The server diffs against the
	 * parent scope and stores only the overrides, so we always submit the full (merged) document.
	 * @param {String} pNamespaceKey
	 * @param {String} pNamespaceValue
	 * @param {Object} pConfig - the complete config object for that value
	 */
	async _putConfig(pNamespaceKey, pNamespaceValue, pConfig)
	{
		const tmpLevel = this._level();
		const tmpURL = `Configuration/Put/${pNamespaceKey}/${encodeURIComponent(pNamespaceValue)}/${tmpLevel.Context}/${tmpLevel.ContextID}`;
		return this._postJSON(tmpURL, { Config: pConfig });
	}

	/**
	 * Point the controller at a scope + permission and register the host's re-render.
	 * @param {{IDProject:Number, IDCustomer:Number}} pScope
	 * @param {Boolean} pCanManage
	 * @param {Function} pOnChange
	 */
	setContext(pScope, pCanManage, pOnChange)
	{
		this.scope = pScope || { IDProject: 0, IDCustomer: 0 };
		this.canManage = !!pCanManage;
		this._onChange = (typeof pOnChange === 'function') ? pOnChange : () => {};
		this._doc = {};
		this._distill = { DistillingEnabled: false, ByType: {} };
		this.types = [];
		this.selectedType = '';
	}

	/** The label (EntityType==='Label') PropertyInfo entries of one observation type, as [ [TagType, entry], ... ]. */
	_labelEntries(pType)
	{
		const tmpTypeConfig = this._doc[pType];
		const tmpPropertyInfo = tmpTypeConfig && tmpTypeConfig.PropertyInfo;
		if (!tmpPropertyInfo || (typeof tmpPropertyInfo !== 'object')) { return []; }
		return Object.keys(tmpPropertyInfo)
			.map((pTagType) => [ pTagType, tmpPropertyInfo[pTagType] ])
			.filter((pPair) => pPair[1] && (pPair[1].EntityType === 'Label'));
	}

	/** Recompute the type list (only observation types that carry at least one label) from the loaded doc. */
	_rebuildTypes()
	{
		this.types = Object.keys(this._doc)
			.filter((pKey) => !NON_TYPE_KEYS.has(pKey))
			.map((pType) =>
			{
				const tmpLabels = this._labelEntries(pType);
				const tmpConfig = this._doc[pType] || {};
				const tmpDisplay = tmpConfig.ObservationDisplayName || tmpConfig.DisplayName || pType;
				return { Type: pType, DisplayName: tmpDisplay, LabelCount: tmpLabels.length };
			})
			.filter((pEntry) => pEntry.LabelCount > 0)
			.sort((a, b) => String(a.DisplayName || '').localeCompare(String(b.DisplayName || '')));
		// Keep a valid selection.
		if (!this.types.some((pEntry) => pEntry.Type === this.selectedType))
		{
			this.selectedType = this.types.length ? this.types[0].Type : '';
		}
	}

	// --- Data ops ---

	async load()
	{
		this.loading = true;
		this._onChange();
		try
		{
			const tmpDoc = await this._getConfig(OBS_NAMESPACE);
			this._doc = (tmpDoc && typeof tmpDoc === 'object') ? tmpDoc : {};
			// The distilling switches live in their own namespace; best-effort (absent = all off).
			try
			{
				const tmpDistillDoc = await this._getConfig(DISTILL_NAMESPACE);
				const tmpLibrary = (tmpDistillDoc && (tmpDistillDoc.Library || (tmpDistillDoc.Config && tmpDistillDoc.Config.Library))) || {};
				this._distill = { DistillingEnabled: !!tmpLibrary.DistillingEnabled, ByType: (tmpLibrary.ByType && typeof tmpLibrary.ByType === 'object') ? tmpLibrary.ByType : {} };
			}
			catch (pError) { this._distill = { DistillingEnabled: false, ByType: {} }; }
			this._rebuildTypes();
		}
		catch (pError)
		{
			this._doc = {};
			this.types = [];
			this._toast(`Could not load label behaviors: ${pError.message || pError}`, 'error');
		}
		this.loading = false;
		this._onChange();
	}

	/** Switch the active observation type (client-side; the config for every type is already loaded). */
	selectType(pType)
	{
		if (this._doc[pType]) { this.selectedType = pType; this._onChange(); }
	}

	/** Whether Auto-Filter (distilling) is on for the selected type (per-type override, else the scope default). */
	_distillingEnabledFor(pType)
	{
		const tmpByType = this._distill.ByType || {};
		if (tmpByType[pType] && (typeof tmpByType[pType].DistillingEnabled === 'boolean')) { return tmpByType[pType].DistillingEnabled; }
		return !!this._distill.DistillingEnabled;
	}

	/** Guard + fetch the PropertyInfo entry for a (type, tagType); returns null when not editable/found. */
	_entry(pType, pTagType)
	{
		if (!this.canManage) { return null; }
		const tmpTypeConfig = this._doc[pType];
		const tmpEntry = tmpTypeConfig && tmpTypeConfig.PropertyInfo && tmpTypeConfig.PropertyInfo[pTagType];
		return (tmpEntry && (tmpEntry.EntityType === 'Label')) ? tmpEntry : null;
	}

	/** Persist the selected observation type's config (read-merge-write of the whole value) after a mutation. */
	async _saveType(pType, pDescription)
	{
		this.busy = true;
		this._onChange();
		try
		{
			await this._putConfig(OBS_NAMESPACE, pType, this._doc[pType]);
			this._toast(pDescription || 'Label behavior saved.');
			this._requestConfigSync();
		}
		catch (pError)
		{
			this._toast(`Could not save: ${pError.message || pError}. Reloading.`, 'error');
			await this.load();
		}
		this.busy = false;
		this._onChange();
	}

	/** Deny (or allow) free entry for a label type on the selected observation type. deny=true → FreeEntry:false. */
	async setFreeEntry(pTagType, pDeny)
	{
		const tmpEntry = this._entry(this.selectedType, pTagType);
		if (!tmpEntry) { return; }
		tmpEntry.FreeEntry = !pDeny;
		await this._saveType(this.selectedType, pDeny ? `Free entry denied for “${pTagType}”.` : `Free entry allowed for “${pTagType}”.`);
	}

	/** Set the per-observation max count for a label type. 0 / blank = unlimited (stored explicitly as 0). */
	async setMaxCount(pTagType, pValue)
	{
		const tmpEntry = this._entry(this.selectedType, pTagType);
		if (!tmpEntry) { return; }
		let tmpN = parseInt(pValue, 10);
		if (!Number.isFinite(tmpN) || tmpN < 0) { tmpN = 0; }
		tmpEntry.MaxCount = tmpN;
		await this._saveType(this.selectedType, (tmpN > 0) ? `Max ${tmpN} “${pTagType}” per observation.` : `“${pTagType}” set to unlimited.`);
	}

	/** Hide (or show) a label type from the entry UI on the selected observation type. */
	async setHidden(pTagType, pHidden)
	{
		const tmpEntry = this._entry(this.selectedType, pTagType);
		if (!tmpEntry) { return; }
		tmpEntry.Hidden = !!pHidden;
		await this._saveType(this.selectedType, pHidden ? `“${pTagType}” hidden from entry.` : `“${pTagType}” shown in entry.`);
	}

	/** Toggle Auto-Filter (distilling) for the whole selected observation type (its own namespace/blob). */
	async setDistilling(pEnabled)
	{
		if (!this.canManage || !this.selectedType) { return; }
		const tmpByType = this._distill.ByType || (this._distill.ByType = {});
		tmpByType[this.selectedType] = Object.assign({}, tmpByType[this.selectedType], { DistillingEnabled: !!pEnabled });
		this.busy = true;
		this._onChange();
		try
		{
			await this._putConfig(DISTILL_NAMESPACE, DISTILL_VALUE, { DistillingEnabled: !!this._distill.DistillingEnabled, ByType: tmpByType });
			this._toast(pEnabled ? `Auto-Filter on for “${this.selectedType}”.` : `Auto-Filter off for “${this.selectedType}”.`);
			this._requestConfigSync();
		}
		catch (pError)
		{
			this._toast(`Could not save Auto-Filter: ${pError.message || pError}. Reloading.`, 'error');
			await this.load();
		}
		this.busy = false;
		this._onChange();
	}

	// --- Display ---

	_escape(pValue)
	{
		return String(pValue == null ? '' : pValue).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar]));
	}

	/** Escape a value for use inside a single-quoted JS string literal in an inline handler attribute. */
	_jsString(pValue)
	{
		return String(pValue == null ? '' : pValue).replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/[&<>"]/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[pChar]));
	}

	/** Build the render record the shared Headlight-LabelBehavior template consumes. */
	buildState()
	{
		const tmpHasTypes = this.types.length > 0;
		const tmpType = this.selectedType;
		const tmpRows = (!this.loading && tmpHasTypes)
			? this._labelEntries(tmpType).map((pPair) =>
			{
				const tmpTagType = pPair[0];
				const tmpEntry = pPair[1];
				const tmpMax = Number(tmpEntry.MaxCount) || 0;
				const tmpNameText = (tmpEntry.DisplayName != null && String(tmpEntry.DisplayName).trim() !== '') ? tmpEntry.DisplayName : tmpTagType;
				return {
					ProviderName: this.providerName,
					TagTypeJS: this._jsString(tmpTagType),
					TagType: this._escape(tmpTagType),
					DisplayName: this._escape(tmpNameText),
					DenyChecked: (tmpEntry.FreeEntry === false) ? 'checked' : '',
					MaxValue: (tmpMax > 0) ? String(tmpMax) : '',
					HiddenChecked: (tmpEntry.Hidden === true) ? 'checked' : '',
					Disabled: (this.canManage && !this.busy) ? '' : 'disabled',
				};
			})
			: [];
		const tmpTypeOptions = this.types.map((pEntry) => ({
			Value: this._escape(pEntry.Type),
			Label: `${this._escape(pEntry.DisplayName)}${(pEntry.DisplayName !== pEntry.Type) ? ` (${this._escape(pEntry.Type)})` : ''}`,
			Selected: (pEntry.Type === tmpType) ? 'selected' : '',
		}));
		return {
			ProviderName: this.providerName,
			LoadingSlot: this.loading ? [ {} ] : [],
			ReadOnlySlot: (!this.canManage && !this.loading) ? [ {} ] : [],
			NoTypesSlot: (!this.loading && !tmpHasTypes) ? [ {} ] : [],
			PickerSlot: (!this.loading && tmpHasTypes) ? [ { ProviderName: this.providerName, Options: tmpTypeOptions, Disabled: this.busy ? 'disabled' : '' } ] : [],
			DistillingSlot: (!this.loading && tmpHasTypes) ? [ {
				ProviderName: this.providerName,
				DistillChecked: this._distillingEnabledFor(tmpType) ? 'checked' : '',
				Disabled: (this.canManage && !this.busy) ? '' : 'disabled',
			} ] : [],
			TableSlot: (!this.loading && tmpHasTypes && tmpRows.length > 0) ? [ { Rows: tmpRows } ] : [],
			EmptyTypeSlot: (!this.loading && tmpHasTypes && tmpRows.length === 0) ? [ {} ] : [],
		};
	}
}

module.exports = LabelBehaviorController;

module.exports.LABELBEHAVIOR_CSS = /*css*/`
	.lbh-mgr { font-size: 0.92rem; }
	.lbh-toolbar { display: flex; align-items: center; flex-wrap: wrap; gap: 0.9rem; margin: 0 0 0.9rem; }
	.lbh-field { display: flex; align-items: center; gap: 0.4rem; }
	.lbh-field label { font-size: 0.66rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--theme-color-text-muted, #6b7686); }
	.lbh-select { min-width: 15rem; }
	.lbh-distill { margin-left: auto; display: flex; align-items: center; gap: 0.45rem; background: var(--theme-color-background-tertiary, #f4f6f9);
		border: 1px solid var(--theme-color-border-light, #e8ebf0); border-radius: 8px; padding: 0.35rem 0.7rem; }
	.lbh-distill .lbh-distill-label { font-weight: 650; color: var(--theme-color-text-secondary, #45505f); }
	.lbh-distill .lbh-distill-hint { color: var(--theme-color-text-muted, #6b7686); font-size: 0.8rem; }
	.lbh-table { width: 100%; border-collapse: separate; border-spacing: 0; border: 1px solid var(--theme-color-border-light, #e8ebf0); border-radius: 8px; overflow: hidden; }
	.lbh-table th { text-align: left; font-size: 0.66rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--theme-color-text-muted, #6b7686);
		background: var(--theme-color-background-tertiary, #f4f6f9); padding: 0.45rem 0.8rem; border-bottom: 1px solid var(--theme-color-border-light, #e8ebf0); }
	.lbh-th-type { width: 34%; }
	.lbh-th-center { text-align: center; width: 14%; }
	.lbh-th-max { width: 16%; }
	.lbh-table td { padding: 0.4rem 0.8rem; border-bottom: 1px solid var(--theme-color-border-light, #eef1f5); vertical-align: middle; }
	.lbh-table tbody tr:last-child td { border-bottom: none; }
	.lbh-type { font-weight: 650; color: var(--theme-color-text-primary, #1f2733); }
	.lbh-td-center { text-align: center; }
	.lbh-maxinput { max-width: 6rem; }
	.lbh-note { font-size: 0.84rem; color: var(--theme-color-text-muted, #6b7686); font-style: italic; margin-top: 0.6rem; }
	.lbh-loading { color: var(--theme-color-text-muted, #6b7686); padding: 0.8rem 0.2rem; }
	.lbh-spin { display: inline-block; width: 1rem; height: 1rem; border: 2px solid var(--theme-color-border-default, #dbdbdb); border-top-color: var(--theme-color-brand-primary, #5650e6); border-radius: 50%; animation: lbh-spin 0.7s linear infinite; vertical-align: -0.15rem; margin-right: 0.4rem; }
	@keyframes lbh-spin { to { transform: rotate(360deg); } }
`;

module.exports.LABELBEHAVIOR_TEMPLATES =
[
	{
		Hash: 'Headlight-LabelBehavior',
		Template: /*html*/`
			<div class="lbh-mgr">
				{~TS:Headlight-LabelBehaviorLoading:Record.LoadingSlot~}
				{~TS:Headlight-LabelBehaviorNoTypes:Record.NoTypesSlot~}
				<div class="lbh-toolbar">
					{~TS:Headlight-LabelBehaviorPicker:Record.PickerSlot~}
					{~TS:Headlight-LabelBehaviorDistilling:Record.DistillingSlot~}
				</div>
				{~TS:Headlight-LabelBehaviorTable:Record.TableSlot~}
				{~TS:Headlight-LabelBehaviorEmptyType:Record.EmptyTypeSlot~}
				{~TS:Headlight-LabelBehaviorReadOnly:Record.ReadOnlySlot~}
			</div>`
	},
	{
		Hash: 'Headlight-LabelBehaviorPicker',
		Template: /*html*/`
			<div class="lbh-field">
				<label>Observation type</label>
				<div class="select is-small">
					<select class="lbh-select" {~D:Record.Disabled~} onchange="_Pict.providers['{~D:Record.ProviderName~}'].labelBehaviorSelectType(this.value)">
						{~TS:Headlight-LabelBehaviorPickerOption:Record.Options~}
					</select>
				</div>
			</div>`
	},
	{
		Hash: 'Headlight-LabelBehaviorPickerOption',
		Template: /*html*/`<option value="{~D:Record.Value~}" {~D:Record.Selected~}>{~D:Record.Label~}</option>`
	},
	{
		Hash: 'Headlight-LabelBehaviorDistilling',
		Template: /*html*/`
			<div class="lbh-distill" title="Auto-Filter cascades each label's pick list by the other labels already chosen on the observation.">
				<label class="checkbox lbh-distill-label"><input type="checkbox" {~D:Record.DistillChecked~} {~D:Record.Disabled~} onchange="_Pict.providers['{~D:Record.ProviderName~}'].labelBehaviorSetDistilling(this.checked)" /> Auto-Filter</label>
				<span class="lbh-distill-hint">(distilling for this type)</span>
			</div>`
	},
	{
		Hash: 'Headlight-LabelBehaviorTable',
		Template: /*html*/`
			<table class="lbh-table">
				<thead><tr>
					<th class="lbh-th-type">Label type</th>
					<th class="lbh-th-center">Deny free entry</th>
					<th class="lbh-th-max">Max / observation</th>
					<th class="lbh-th-center">Hidden</th>
				</tr></thead>
				<tbody>{~TS:Headlight-LabelBehaviorRow:Record.Rows~}</tbody>
			</table>`
	},
	{
		Hash: 'Headlight-LabelBehaviorRow',
		Template: /*html*/`
			<tr class="lbh-row">
				<td class="lbh-type">{~D:Record.DisplayName~}</td>
				<td class="lbh-td-center"><input type="checkbox" {~D:Record.DenyChecked~} {~D:Record.Disabled~} onchange="_Pict.providers['{~D:Record.ProviderName~}'].labelBehaviorSetFreeEntry('{~D:Record.TagTypeJS~}', this.checked)" /></td>
				<td><input class="input is-small lbh-maxinput" type="number" min="0" step="1" placeholder="∞" value="{~D:Record.MaxValue~}" {~D:Record.Disabled~} onchange="_Pict.providers['{~D:Record.ProviderName~}'].labelBehaviorSetMaxCount('{~D:Record.TagTypeJS~}', this.value)" /></td>
				<td class="lbh-td-center"><input type="checkbox" {~D:Record.HiddenChecked~} {~D:Record.Disabled~} onchange="_Pict.providers['{~D:Record.ProviderName~}'].labelBehaviorSetHidden('{~D:Record.TagTypeJS~}', this.checked)" /></td>
			</tr>`
	},
	{
		Hash: 'Headlight-LabelBehaviorEmptyType',
		Template: /*html*/`<p class="lbh-note">This observation type has no labels to configure.</p>`
	},
	{
		Hash: 'Headlight-LabelBehaviorNoTypes',
		Template: /*html*/`<p class="lbh-note">No observation types with labels are available at this scope yet.</p>`
	},
	{
		Hash: 'Headlight-LabelBehaviorReadOnly',
		Template: /*html*/`<p class="lbh-note">You don’t have permission to change label behaviors.</p>`
	},
	{
		Hash: 'Headlight-LabelBehaviorLoading',
		Template: /*html*/`<div class="lbh-loading"><span class="lbh-spin"></span>Loading label behaviors…</div>`
	}
];
